// src/components/activities/ActivitiesList.tsx
import React, { useEffect, useState } from 'react';
import {
  Table, Tag, Space, Button, Empty, Skeleton, Typography, Tooltip, Avatar, Timeline,
  Dropdown, Badge, Modal, message, type MenuProps
} from 'antd';
import {
  CalendarOutlined, EyeOutlined, EyeInvisibleOutlined, MoreOutlined,
  ClockCircleOutlined, UserOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/it';
import type { ActivityDTO, Page } from '../../services/activities';
import { deleteActivity, updateActivity } from '../../services/activities';
import ActivityDetailsDrawer from './ActivityDetailsDrawer';
import CreateActivityDrawer from './CreateActivityDrawer';
import { renderAntdIcon } from '../../utils/antdIcon';
import { getMe, type UserDTO } from '../../services/users';

dayjs.extend(relativeTime);
dayjs.locale('it');

const { Text, Paragraph } = Typography;

/* -------------------- Props -------------------- */
export type Props = {
  data: Page<ActivityDTO> | null;
  loading?: boolean;
  onRefresh?: () => void;
  onEdit?: (activity: ActivityDTO) => void; // opzionale: se fornito lo usiamo al posto del drawer interno
  onPageChange?: (page: number, pageSize: number) => void;
  viewMode?: 'table' | 'timeline';
  showHeader?: boolean; // no Card wrapper
  className?: string;
};

const ActivitiesList: React.FC<Props> = ({
  data,
  loading = false,
  onRefresh,
  onEdit,
  onPageChange,
  viewMode = 'table',
  showHeader = false,
  className = ''
}) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState<number | undefined>(undefined);

  const [me, setMe] = useState<UserDTO | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  useEffect(() => {
    getMe().then(setMe).catch(() => setMe(null));
  }, []);

  const activities = data?.content || [];
  const totalElements = data?.totalElements || 0;
  const currentPage = (data?.number || 0) + 1;
  const pageSize = data?.size || 10;

  const canManage = (rec: ActivityDTO) => {
    const role = (me?.role || '').toUpperCase();
    const isOwner = !!me && me.id === rec.userId;
    const isAdminOrPjm = role === 'ADMIN' || role === 'PJM';
    return isOwner || isAdminOrPjm;
  };

  const openDetails = (id?: number | null) => {
    if (!id) return;
    setSelectedId(id);
    setDetailsOpen(true);
  };

  const openEdit = (id: number) => {
    // Se il genitore fornisce onEdit, deleghiamo a lui
    if (onEdit) {
      const rec = activities.find(a => a.id === id);
      if (rec) onEdit(rec);
      return;
    }
    setEditId(id);
    setEditOpen(true);
  };

  async function handleDelete(rec: ActivityDTO) {
    if (!canManage(rec)) return;

    Modal.confirm({
      title: 'Eliminare questa attività?',
      content: (
        <div>
          <div><strong>{rec.name || rec.activityType?.label || rec.activityType?.code || 'Attività'}</strong></div>
          <div style={{ color: '#8c8c8c' }}>
            {dayjs(rec.occurredAt).format('DD/MM/YYYY HH:mm')}
          </div>
        </div>
      ),
      okText: 'Elimina',
      okButtonProps: { danger: true, loading: deletingId === rec.id },
      cancelText: 'Annulla',
      onOk: async () => {
        try {
          setDeletingId(rec.id);
          await deleteActivity(rec.id);
          message.success('Attività eliminata');
          onRefresh?.();
        } catch (e: any) {
          message.error(e?.message || 'Errore durante l’eliminazione');
        } finally {
          setDeletingId(null);
        }
      },
    });
  }

  async function handleToggleVisibility(rec: ActivityDTO) {
    if (!canManage(rec)) return;
    const nextVis = !rec.visibility;
    try {
      setTogglingId(rec.id);
      // L'update richiede (di solito) il payload completo base:
      await updateActivity(rec.id, {
        name: rec.name,
        description: rec.description ?? undefined,
        customerId: rec.customerId!,
        occurredAt: rec.occurredAt,
        visibility: nextVis,
      });
      message.success(nextVis ? 'Attività resa visibile' : 'Attività resa non visibile');
      onRefresh?.();
    } catch (e: any) {
      message.error(e?.message || 'Errore aggiornamento visibilità');
    } finally {
      setTogglingId(null);
    }
  }

  const getActionItems = (record: ActivityDTO): MenuProps['items'] => {
    const manageable = canManage(record);
    const items: MenuProps['items'] = [
      ...(manageable ? [{ key: 'edit', icon: <EditOutlined />, label: 'Modifica' }] : []),
      {
        key: 'toggleVis',
        icon: record.visibility ? <EyeInvisibleOutlined /> : <EyeOutlined />,
        label: record.visibility ? 'Rendi non visibile' : 'Rendi visibile',
        disabled: !manageable || togglingId === record.id,
      },
      ...(manageable
        ? [{
            key: 'delete',
            icon: <DeleteOutlined />,
            label: 'Elimina',
            danger: true,
            disabled: deletingId === record.id,
          }]
        : []),
      { type: 'divider' },
      { key: 'open', icon: <EyeOutlined />, label: 'Apri dettagli' }
    ];
    return items;
  };

  const columns = [
    {
      title: 'Titolo',
      key: 'activity',
      width: 420,
      render: (record: ActivityDTO) => {
        const icon = renderAntdIcon(record.activityType?.icon);
        const label = record.activityType?.label || record.activityType?.code || '—';
        return (
          <div className="activity-cell" style={{ display: 'flex', gap: 12 }}>
            <Avatar style={{ background: '#f0f5ff', color: '#1677ff' }} icon={icon} />
            <div style={{ flex: 1 }}>
              <div className="activity-header" style={{ marginBottom: 4 }}>
                <Text strong style={{ fontSize: 14 }}>{record.name || label}</Text>
                {record.visibility ? (
                  <Tooltip title="Visibile al cliente">
                    <EyeOutlined style={{ color: '#52c41a', marginLeft: 8 }} />
                  </Tooltip>
                ) : (
                  <Tooltip title="Privata">
                    <EyeInvisibleOutlined style={{ color: '#ff4d4f', marginLeft: 8 }} />
                  </Tooltip>
                )}
              </div>

              {record.description && (
                <Paragraph
                  ellipsis={{ rows: 2, expandable: false }}
                  style={{ margin: '4px 0 0', color: '#8c8c8c', fontSize: 13 }}
                >
                  {record.description}
                </Paragraph>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Data',
      key: 'date',
      width: 220,
      render: (record: ActivityDTO) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 13 }}>
            <CalendarOutlined style={{ marginRight: 4 }} />
            {dayjs(record.occurredAt).format('DD MMM YYYY')}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <ClockCircleOutlined style={{ marginRight: 4 }} />
            {dayjs(record.occurredAt).format('HH:mm')}
          </Text>
          <Text type="secondary" style={{ fontSize: 11, fontStyle: 'italic' }}>
            {dayjs(record.occurredAt).fromNow()}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Allegati',
      key: 'attachments',
      width: 120,
      align: 'center' as const,
      render: (record: ActivityDTO) => {
        const count = record.attachments?.length || 0;
        if (count === 0) return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
        return (
          <Tooltip title={`${count} allegat${count === 1 ? 'o' : 'i'}`}>
            <Badge count={count} showZero={false} size="small">
              <PaperClipOutlined style={{ fontSize: 24, color: '#8c8c8c' }} />
            </Badge>
          </Tooltip>
        );
      },
    },
    {
      title: 'Utente',
      key: 'user',
      width: 240,
      render: (record: ActivityDTO) => (
        <Tooltip title={`${record.userEmail ?? ''}`}>
          <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
          <Text style={{ marginLeft: 8, fontSize: 13 }}>
            {record.userFirstName || record.userLastName ? (
              <>
                {record.userFirstName} {record.userLastName}
              </>
            ) : (
              <Text type="secondary" style={{ fontStyle: 'italic' }}>Utente sconosciuto</Text>
            )}
          </Text>
        </Tooltip>
      ),
    },
    {
      key: 'actions',
      width: 60,
      align: 'right' as const,
      onCell: () => ({
        onClick: (e: React.MouseEvent) => e.stopPropagation(),
        onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
        onPointerDown: (e: React.PointerEvent) => e.stopPropagation(),
      }),
      render: (record: ActivityDTO) => (
        <Dropdown
          trigger={['click']}
          menu={{
            items: getActionItems(record),
            onClick: async ({ key, domEvent }) => {
              domEvent.stopPropagation();
              if (key === 'edit') {
                if (canManage(record)) openEdit(record.id);
                else openDetails(record.id);
              }
              if (key === 'delete') await handleDelete(record);
              if (key === 'open') openDetails(record.id);
              if (key === 'toggleVis') await handleToggleVisibility(record);
            }
          }}
        >
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          />
        </Dropdown>
      )
    }
  ];

  const renderTimeline = () => {
    if (loading) return <Skeleton active paragraph={{ rows: 4 }} />;
    if (!activities.length) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Nessuna attività registrata"
        >
          {onRefresh && (
            <Button icon={<ReloadOutlined />} onClick={onRefresh} type="primary">
              Ricarica
            </Button>
          )}
        </Empty>
      );
    }

    const grouped = activities.reduce((acc, a) => {
      const date = dayjs(a.occurredAt).format('DD MMMM YYYY');
      (acc[date] ||= []).push(a);
      return acc;
    }, {} as Record<string, ActivityDTO[]>);

    return (
      <div className="activities-timeline">
        {Object.entries(grouped).map(([date, list]) => (
          <div key={date} style={{ marginBottom: 24 }}>
            <div style={{ marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #f0f0f0' }}>
              <Text strong style={{ fontSize: 14, color: '#595959' }}>
                <CalendarOutlined style={{ marginRight: 8 }} />
                {date}
              </Text>
            </div>
            <Timeline>
              {list.map((activity) => (
                <Timeline.Item
                  key={activity.id}
                  dot={
                    <Avatar
                      size="small"
                      style={{ backgroundColor: '#f0f5ff', color: '#1677ff' }}
                      icon={renderAntdIcon(activity.activityType?.icon)}
                    />
                  }
                >
                  <div
                    onClick={() => (canManage(activity) ? openEdit(activity.id) : openDetails(activity.id))}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: '1px solid #f0f0f0',
                      background: '#fff',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div style={{ flex: 1 }}>
                        <Space size={8} style={{ marginBottom: 4 }}>
                          <Text strong style={{ fontSize: 14 }}>
                            {activity.name || activity.activityType?.label || activity.activityType?.code || '—'}
                          </Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            <ClockCircleOutlined style={{ marginRight: 4 }} />
                            {dayjs(activity.occurredAt).format('HH:mm')}
                          </Text>
                        </Space>
                        {activity.description && (
                          <Paragraph style={{ margin: '6px 0 0', color: '#595959', fontSize: 13 }}>
                            {activity.description}
                          </Paragraph>
                        )}
                        {activity.attachments?.length > 0 && (
                          <div style={{ marginTop: 6 }}>
                            <Tag color="blue">
                              {activity.attachments.length} allegat{activity.attachments.length === 1 ? 'o' : 'i'}
                            </Tag>
                          </div>
                        )}
                      </div>
                      <Dropdown
                        trigger={['click']}
                        menu={{
                          items: getActionItems(activity),
                          onClick: async ({ key, domEvent }) => {
                            domEvent.stopPropagation();
                            if (key === 'edit') {
                              if (canManage(activity)) openEdit(activity.id);
                              else openDetails(activity.id);
                            }
                            if (key === 'delete') await handleDelete(activity);
                            if (key === 'open') openDetails(activity.id);
                            if (key === 'toggleVis') await handleToggleVisibility(activity);
                          }
                        }}
                      >
                        <Button
                          type="text"
                          icon={<MoreOutlined />}
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                          onPointerDown={(e) => e.stopPropagation()}
                        />
                      </Dropdown>
                    </div>
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          </div>
        ))}
      </div>
    );
  };

  if (loading) return <Skeleton active paragraph={{ rows: 6 }} />;

  if (!activities.length) {
    return (
      <Empty description="Nessuna attività" image={Empty.PRESENTED_IMAGE_SIMPLE}>
        {onRefresh && (
          <Button icon={<ReloadOutlined />} onClick={onRefresh} type="primary">
            Ricarica
          </Button>
        )}
      </Empty>
    );
  }

  return (
    <>
      {showHeader && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <Text strong>Attività</Text>
          {onRefresh && <Button onClick={onRefresh}>Aggiorna</Button>}
        </div>
      )}

      {viewMode === 'table' ? (
        <Table
          rowKey="id"
          columns={columns as any}
          dataSource={activities}
          pagination={{
            current: currentPage,
            pageSize,
            total: totalElements,
            showSizeChanger: false,
            onChange: (p) => onPageChange && onPageChange(p, pageSize)
          }}
          onRow={(rec) => ({
            onClick: () => (canManage(rec) ? openEdit(rec.id) : openDetails(rec.id))
          })}
          scroll={{ x: 800 }}
        />
      ) : (
        renderTimeline()
      )}

      {/* Drawer DETTAGLI (sempre disponibile) */}
      <ActivityDetailsDrawer
        open={detailsOpen}
        activityId={selectedId ?? undefined}
        onClose={() => setDetailsOpen(false)}
      />

      {/* Drawer EDIT interno (se non viene fornito onEdit) */}
      {!onEdit && (
        <CreateActivityDrawer
          open={editOpen}
          onClose={() => { setEditOpen(false); setEditId(undefined); }}
          onUpdated={() => {
            setEditOpen(false);
            setEditId(undefined);
            onRefresh?.();
          }}
          mode="edit"
          activityId={editId}
          lockType
        />
      )}
    </>
  );
};

export default ActivitiesList;
